import { setTranslations } from './translations'
import { mountWidget } from './widget'
import { mountLoginButton, type LoginOptions } from './login'
import type { OwnIdMock, StartOptions, TranslationInput } from './types'

/**
 * AeroPass BE endpoint. The SDK is env-agnostic: the consuming app owns the URL
 * and sets `window.AEROSTUB_BE_URL` to its AeroAuth API **base** (from its own
 * per-environment config, e.g. VITE_AWS_AEROAUTH_API) before the SDK loads — so
 * one artifact serves qa/staging/sandbox. The SDK appends its own route. The
 * baked VITE_AEROPASS_BE_URL is only a dev/playground fallback.
 */
const ENV_BE_URL = (import.meta.env.VITE_AEROPASS_BE_URL as string) || ''
const STUB_ROUTE = '/aeroStubSession'

function resolveApiUrl(): string {
  const base =
    (window as unknown as { AEROSTUB_BE_URL?: string }).AEROSTUB_BE_URL ||
    ENV_BE_URL
  return base ? base + STUB_ROUTE : ''
}

function start(options: StartOptions): void {
  if (!options || !options.container) {
    console.error('[ownid-mock] start() requires a `container` selector')
    return
  }
  const apiUrl = resolveApiUrl()
  if (!apiUrl) {
    console.error(
      '[ownid-mock] no AeroPass BE endpoint configured (VITE_AEROPASS_BE_URL)'
    )
  }
  mountWidget({
    container: options.container,
    loginId: String(options.loginId ?? ''),
    apiUrl,
    providers: options.providers,
    events: options.events,
  })
}

function login(options: LoginOptions): void {
  const apiUrl = resolveApiUrl()
  if (!apiUrl) {
    console.error('[ownid-mock] no AeroPass BE endpoint configured (VITE_AEROPASS_BE_URL)')
  }
  mountLoginButton(options, apiUrl)
}

/** Dispatch one `ownid(action, options)` call. */
function handle(action: string, options?: unknown): void {
  switch (action) {
    case 'setTranslations':
      setTranslations((options as TranslationInput) ?? {})
      break
    case 'init':
      // OwnID's init({ appId, language, … }) configures the real SDK. The mock
      // has nothing to apply (endpoint is env-baked), so it's a compatible no-op.
      break
    case 'start':
      start(options as StartOptions)
      break
    case 'login':
      login(options as LoginOptions)
      break
    default:
      // The real SDK supports more actions; the app only uses the above.
      console.warn(`[ownid-mock] unsupported action "${action}" (no-op)`)
  }
}

/**
 * Build the callable global. It mirrors the real SDK's dual nature: a function
 * AND an object carrying `headless`/`platform`, which the app probes to decide
 * whether 2FA can run inside the iframe. We always report "yes" so the OTP form
 * renders in place (no popup), which is what we want in lower environments.
 */
export function createOwnId(): OwnIdMock {
  const fn = ((action: string, options?: unknown) => {
    try {
      handle(action, options)
    } catch (err) {
      console.error(`[ownid-mock] action "${action}" failed`, err)
    }
    return { error: null, data: null }
  }) as OwnIdMock

  fn.headless = {
    withContext: () => ({
      auth: {
        // PasskeyAuth present => canAuthenticateInIframe() returns true => iframe.
        discover: async () => ({ operations: [{ type: 'PasskeyAuth' }] }),
      },
    }),
  }

  fn.platform = {
    passkeys: {
      isAvailable: async () => ({ available: true }),
    },
  }

  return fn
}
